// DivetIQ marketing copy. Single source of truth - all section components read from here.

/** Canonical site URL. Single source of truth for absolute URLs across SEO files. */
export const SITE_URL = (
  process.env.NEXT_PUBLIC_SITE_URL ?? "https://www.divetiq.com"
).replace(/\/$/, "");

export const CONTACT_RECIPIENTS = [
  "Kerim.Redzepagic@artcogroup.ba",
  "anis.skamo@artcogroup.ba",
  "ibrahim.mesan@artcogroup.ba",
] as const;

/** Comma-separated for `mailto:` To field (contact form + footer email). */
export const CONTACT_RECIPIENT = CONTACT_RECIPIENTS.join(",");

/**
 * Canonical company contact details. Surfaced on the /contact page, in the
 * footer, and in Organization JSON-LD for SEO.
 *
 * Phone is stored in E.164 (`+19034596068`) for `tel:` hrefs and in a
 * human-readable form for display.
 */
export const COMPANY = {
  headquarters: {
    label: "Headquarters",
    streetLine1: "2810 N Church St",
    streetLine2: "STE 88941",
    city: "Wilmington",
    region: "Delaware",
    regionCode: "DE",
    postalCode: "19802",
    country: "United States",
    countryCode: "US",
  },
  offices: [
    {
      city: "Sarajevo",
      country: "Bosnia and Herzegovina",
      countryCode: "BA",
    },
  ],
  phone: {
    e164: "+19034596068",
    display: "+1 (903) 459-6068",
  },
} as const;

/** Placeholder social profile URLs. Replace once real handles exist. */
export const SOCIAL_PROFILES = {
  linkedin: "https://www.linkedin.com/company/divetiq",
  x: "https://x.com/divetiq",
  facebook: "https://www.facebook.com/divetiq",
  email: `mailto:${CONTACT_RECIPIENT}`,
} as const;

export type NavItem = {
  label: string;
  href: string;
  /** `anchor` items participate in scroll-spy on the home page; `page` items are highlighted by route match. */
  kind: "anchor" | "page";
  /** For anchor items, the section element id observed (defaults to the href fragment). */
  sectionId?: string;
};
export type Feature = {
  title: string;
  description: string;
  href: string;
  variant: "contact" | "automation" | "pipeline" | "dashboard";
};
export type Commitment = {
  number: string;
  kicker: string;
  title: string;
  body: string;
  tagline: string;
  tone: "blue" | "purple" | "aqua";
};
export type ModuleCard = {
  slug: string;
  number: string;
  title: string;
  tagline: string;
  accent: "blue" | "aqua" | "green" | "orange" | "purple";
  chips: readonly [string, string, string];
};

/** Structural metadata for an individual module marketing page.
 *  Display strings live in dict.modulePages[slug] for i18n. */
export type ModuleDetail = {
  slug: string;
  /** Canonical pre-locale href. */
  href: string;
  /** Accent (matches MODULES.accent so backgrounds line up). */
  accent: "blue" | "aqua" | "green" | "orange" | "purple";
  /** Number of stat tiles, capability cards. Used by the renderer to enforce list lengths. */
  statCount: 3;
  capabilityCount: 6;
  /** Names of agents from AGENT_CATALOG that ship pre-wired with this module. */
  relatedAgents: readonly string[];
  /** Optional hero screenshot rendered alongside the module hero copy. */
  heroShot?: { src: string; alt: string; aspect?: "16/10" | "4/3" };
  /** Optional looping mid-page screen recording. .webm + .png poster. */
  loopVideo?: { src: string; poster: string; alt: string };
  /** Optional bottom-band screenshot replacing the placeholder mockup band. */
  mockupShot?: { src: string; alt: string };
};
export type WorkflowStep = {
  index: string;
  label: string;
  agents: string;
  headline: string;
  body: string;
  metric?: string;
};
export type AgentCategory = { category: string; agents: readonly string[] };
export type ComparisonRow = {
  row: string;
  divetiq: string;
  workday: string;
  anaplan: string;
};
export type PayCard = {
  number: string;
  kicker: string;
  title: string;
  body: string;
};
export type InvoiceLine = { module: string; detail: string; amount: string };
export type Testimonial = {
  quote: string;
  name: string;
  role: string;
  company: string;
};
export type FooterLink = { label: string; href: string; stub?: boolean };
export type FooterColumn = { heading: string; links: readonly FooterLink[] };
export type FaqItem = {
  question: string;
  answer: string;
  topic: "General";
};

// ─────────────────────────────────────────────────────────────────────────────
// Site
// ─────────────────────────────────────────────────────────────────────────────

export const SITE = {
  name: "DivetIQ",
  tagline: "One Headless Platform. Eight Modules. Pay Per Use.",
  description:
    "DivetIQ unifies Procurement, ECM, HCM, EAM, EPM, ERP, CRM and AI BI into one API-first platform. AI agents watch every KPI and execute remediation. Billed pay per use.",
} as const;

export const NAV_ITEMS: readonly NavItem[] = [
  { label: "Platform", href: "/#platform", kind: "anchor", sectionId: "platform" },
  { label: "How it works", href: "/#workflow", kind: "anchor", sectionId: "workflow" },
  { label: "AI Agents", href: "/agents", kind: "page" },
  { label: "Pricing", href: "/#pricing", kind: "anchor", sectionId: "pricing" },
  { label: "FAQ", href: "/#faq", kind: "anchor", sectionId: "faq" },
  { label: "Blog", href: "/blog", kind: "page" },
];

// ─────────────────────────────────────────────────────────────────────────────
// Hero
// ─────────────────────────────────────────────────────────────────────────────

export const HERO = {
  eyebrow: "ONE PLATFORM · EIGHT MODULES · ZERO LOCK-IN",
  heading: "Headless Software Solution with an",
  headingAccent: "AI Agentic Workflow",
  description:
    "DivetIQ unifies Procurement, HCM, EAM, EPM, ERP, CRM and an AI-native data warehouse into one API-first platform. Specialist agents watch every KPI and execute remediation through the same APIs your people use. Billed pay per use.",
  primary: { label: "Book a demo", href: "/contact" },
  secondary: { label: "How it works", href: "/#workflow" },
  stats: [
    { kicker: "Modules", value: "8" },
    { kicker: "Time to value", value: "1-4 wks" },
    { kicker: "Pricing", value: "Pay per use" },
  ] as const,
} as const;

export const TRUST_STRIP = [
  "Headless",
  "API-first",
  "BPMN 2.0",
  "OpenAPI 3.1",
  "GDPR",
  "ISO 27001",
  "SOC 2 Type II",
  "EU Sovereign Cloud ready",
] as const;

export const HERO_CERT_BADGES = [
  "SOC 2 Type II",
  "ISO 27001",
  "GDPR",
  "EU Sovereign Cloud",
] as const;

// ─────────────────────────────────────────────────────────────────────────────
// Three Commitments (renders inside the existing dark Benefits section)
// ─────────────────────────────────────────────────────────────────────────────

export const COMMITMENTS_INTRO = {
  eyebrow: "THREE COMMITMENTS",
  heading: "Three commitments. No middle ground.",
  description:
    "DivetIQ is built on three architectural decisions - not features bolted on, but the foundation everything else sits on.",
} as const;

export const COMMITMENTS: readonly Commitment[] = [
  {
    number: "01",
    kicker: "Commercial",
    title: "Pay per Use - pay only when value is created.",
    body: "Forget five-year seat licenses and shelfware. DivetIQ meters every transaction, every API call, every agent action, every gigabyte. If a module isn't used this month, it isn't billed this month. Procurement spikes during sourcing season; payroll runs once. Your bill follows your business, not the other way around.",
    tagline: "No licenses to over-buy. No users to under-provision. No surprise renewals.",
    tone: "blue",
  },
  {
    number: "02",
    kicker: "Architecture",
    title: "Headless Software Solution - every capability is an API.",
    body: "DivetIQ ships without a hardcoded UI. Every module exposes its capabilities through REST, GraphQL and gRPC, with OpenAPI 3.1 schemas published for every endpoint and Kafka-grade events for every state change. Build your own portal, embed our React components, plug into Microsoft Teams or your customer app - the backend doesn't care.",
    tagline: "Your interface, your brand, your roadmap - on our backend.",
    tone: "purple",
  },
  {
    number: "03",
    kicker: "Intelligence",
    title: "AI Agentic Workflow for KPI Management.",
    body: "Specialist agents watch the KPIs you've already defined - DSO, OEE, MTBF, NPS, attrition, ARR, on-time delivery, working capital. When a metric breaches its threshold, an agent investigates across every module, attributes the variance to its real cause, drafts the remediation, and - within your governance limits - executes it through the same headless APIs your people use.",
    tagline: "Move from dashboards that report problems to agents that resolve them.",
    tone: "aqua",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Featured Modules (existing 4-card Features section)
// ─────────────────────────────────────────────────────────────────────────────

export const FEATURES_INTRO = {
  eyebrow: "FEATURED MODULES",
  heading:
    "From sourcing to settlement, hire to retire, asset to outcome - one platform.",
  description:
    "Each module is independently deployable, headless, and metered Pay per Use. They share one identity layer, one event bus, and one AI-native data warehouse - so a customer in CRM is the same customer in ERP, in EPM, and in the agent's memory.",
} as const;

export const FEATURES: readonly Feature[] = [
  {
    title: "Procurement Platform",
    description:
      "Sourcing to settlement with supplier intelligence - PEPPOL e-invoicing, three-way match, sanctions screening and ESG attribution.",
    href: "https://divetiq-ui-demo.vercel.app/",
    variant: "contact",
  },
  {
    title: "HCM with Payroll & Workforce",
    description:
      "One HCM. Country packs, not custom code. EU + Balkans + US/UK at launch. Concurrent multi-country payroll on one engine.",
    href: "https://divetiq-ui-demo.vercel.app/",
    variant: "automation",
  },
  {
    title: "AI-Based ERP",
    description:
      "Multi-entity GL, continuous consolidation, agent-driven AP, and subscription billing built in - not bolted on.",
    href: "https://divetiq-ui-demo.vercel.app/",
    variant: "pipeline",
  },
  {
    title: "AI BI & Data Warehouse",
    description:
      "One semantic layer. Conversational analytics. Lakehouse with open formats - the same metric everywhere.",
    href: "https://divetiq-ui-demo.vercel.app/",
    variant: "dashboard",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Eight Modules grid (NEW)
// ─────────────────────────────────────────────────────────────────────────────

export const MODULES_INTRO = {
  eyebrow: "THE PLATFORM",
  heading: "One platform. Eight modules. One semantic layer.",
  description:
    "Independently deployable. Headless. Metered Pay per Use. Sharing one identity layer, one event bus, one master-data service, and one AI-native data warehouse.",
} as const;

export const MODULES: readonly ModuleCard[] = [
  {
    slug: "procurement",
    number: "01",
    title: "Procurement",
    tagline: "Sourcing to settlement with supplier intelligence built in.",
    accent: "blue",
    chips: ["3-way match", "PEPPOL e-invoicing", "Supplier risk"],
  },
  {
    slug: "ecm",
    number: "02",
    title: "ECM / DMS",
    tagline: "One content service for every other module.",
    accent: "purple",
    chips: ["Multi-language OCR", "eIDAS signatures", "ISO 15489"],
  },
  {
    slug: "hcm",
    number: "03",
    title: "HCM with Payroll",
    tagline: "One HCM. Country packs, not custom code.",
    accent: "aqua",
    chips: ["Multi-country payroll", "ATS", "Skills graph"],
  },
  {
    slug: "eam",
    number: "04",
    title: "EAM with Inventory",
    tagline: "Asset health, work execution, and spare parts in one model.",
    accent: "orange",
    chips: ["IoT predictive", "MRO inventory", "EHSQ"],
  },
  {
    slug: "epm",
    number: "05",
    title: "EPM",
    tagline: "Plan, consolidate, forecast, narrate.",
    accent: "green",
    chips: ["Driver-based planning", "Continuous consolidation", "AI narratives"],
  },
  {
    slug: "erp",
    number: "06",
    title: "AI-Based ERP",
    tagline: "The financial and operational backbone - with agentic intelligence baked in.",
    accent: "blue",
    chips: ["Multi-entity GL", "Touchless AP", "Subscription billing"],
  },
  {
    slug: "crm",
    number: "07",
    title: "CRM",
    tagline: "Sales, marketing and service on one customer record.",
    accent: "purple",
    chips: ["AI lead scoring", "CDP Lite", "Multi-channel service"],
  },
  {
    slug: "bi",
    number: "08",
    title: "AI BI & Data Warehouse",
    tagline: "One semantic layer. Every metric, exactly once.",
    accent: "aqua",
    chips: ["Conversational analytics", "Open lakehouse", "Reverse-ETL"],
  },
];

export const MODULE_DETAILS: Record<string, ModuleDetail> = {
  procurement: {
    slug: "procurement",
    href: "/modules/procurement",
    accent: "blue",
    statCount: 3,
    capabilityCount: 6,
    relatedAgents: ["Supplier Performance", "DPO / Payment", "Supplier Sustainability"],
    heroShot: {
      src: "/product/procurement/p2p-flow.png",
      alt: "DivetIQ procurement — full P2P flow stage strip with PR, RFQ, PO, receipt, invoice, approval and payment stages",
      aspect: "16/10",
    },
    loopVideo: {
      src: "/product/procurement/3way-match.webm",
      poster: "/product/procurement/3way-match.png",
      alt: "Three-way match in action: PR opens, linked POs and receipts appear, approval pending card highlighted",
    },
    mockupShot: {
      src: "/product/procurement/pr-list.png",
      alt: "Purchase request list view with classification chips and approval status badges",
    },
  },
  ecm: {
    slug: "ecm",
    href: "/modules/ecm",
    accent: "purple",
    statCount: 3,
    capabilityCount: 6,
    relatedAgents: ["Compliance Training", "Pipeline Health"],
    heroShot: {
      src: "/product/ecm/protokol-overview.png",
      alt: "ECM/DMS dashboard — inbound and outbound document counters with classification chips",
      aspect: "16/10",
    },
    loopVideo: {
      src: "/product/ecm/classify.webm",
      poster: "/product/ecm/classify.png",
      alt: "Incoming document classification — open doc, metadata panel slides in, drag-to-classify",
    },
    mockupShot: {
      src: "/product/ecm/doc-detail.png",
      alt: "Document detail view with metadata side panel and classification chips",
    },
  },
  hcm: {
    slug: "hcm",
    href: "/modules/hcm",
    accent: "aqua",
    statCount: 3,
    capabilityCount: 6,
    relatedAgents: ["Workforce Capacity", "Retention Risk", "Payroll Validation", "Wellbeing", "Compliance Training"],
    heroShot: {
      src: "/product/hcm/employees.png",
      alt: "HCM employee master list with avatars, departments and payroll status",
      aspect: "16/10",
    },
    loopVideo: {
      src: "/product/hcm/employee-360.webm",
      poster: "/product/hcm/employee-360.png",
      alt: "Employee 360 detail drawer with contract, salary, absence balance and payroll-run status",
    },
    mockupShot: {
      src: "/product/hcm/payroll-periods.png",
      alt: "Payroll period calendar with run statuses and approval states",
    },
  },
  eam: {
    slug: "eam",
    href: "/modules/eam",
    accent: "orange",
    statCount: 3,
    capabilityCount: 6,
    relatedAgents: ["Asset Health", "Maintenance", "Inventory Optimization", "Energy Optimization", "Field Service"],
    heroShot: {
      src: "/product/eam/asset-register.png",
      alt: "EAM asset register table with machinery, vehicles and IT equipment categories",
      aspect: "16/10",
    },
    loopVideo: {
      src: "/product/eam/asset-detail.webm",
      poster: "/product/eam/asset-detail.png",
      alt: "Asset detail with depreciation curve and maintenance log",
    },
    mockupShot: {
      src: "/product/eam/depreciation.png",
      alt: "Asset depreciation schedule with book value trajectory",
    },
  },
  epm: {
    slug: "epm",
    href: "/modules/epm",
    accent: "green",
    statCount: 3,
    capabilityCount: 6,
    relatedAgents: ["Forecast", "Budget Variance", "Revenue & Margin", "Treasury", "Carbon Footprint"],
    heroShot: {
      src: "/product/epm/cases.png",
      alt: "EPM active cases and initiatives list with priority sorting",
      aspect: "16/10",
    },
    loopVideo: {
      src: "/product/epm/case-timeline.webm",
      poster: "/product/epm/case-timeline.png",
      alt: "Case timeline expanding with approvals, assignments and comments",
    },
    mockupShot: {
      src: "/product/epm/budget-variance.png",
      alt: "Budget vs actual with variance attribution",
    },
  },
  erp: {
    slug: "erp",
    href: "/modules/erp",
    accent: "blue",
    statCount: 3,
    capabilityCount: 6,
    relatedAgents: ["DSO / Collections", "DPO / Payment", "Cash Position", "Reconciliation", "Subscription Revenue"],
    heroShot: {
      src: "/product/erp/dashboard.png",
      alt: "ERP dashboard with all seven flow tiles: P2P, O2C, R2R, H2R, A2R, I2R, P2F",
      aspect: "16/10",
    },
    loopVideo: {
      src: "/product/erp/flow-tour.webm",
      poster: "/product/erp/flow-tour.png",
      alt: "Flow tour: dashboard → O2C stage strip → posting engine with status badges",
    },
    mockupShot: {
      src: "/product/erp/posting-engine.png",
      alt: "GL posting engine status board with batch statuses",
    },
  },
  crm: {
    slug: "crm",
    href: "/modules/crm",
    accent: "purple",
    statCount: 3,
    capabilityCount: 6,
    relatedAgents: ["Pipeline Health", "Customer Health", "Service Performance", "Marketing Performance", "Onboarding"],
    heroShot: {
      src: "/product/crm/dashboard.png",
      alt: "CRM dashboard with opportunities pipeline, lead scoring, activity feed, customer 360 and forecast tiles",
      aspect: "16/10",
    },
    loopVideo: {
      src: "/product/crm/next-best-action.webm",
      poster: "/product/crm/next-best-action.png",
      alt: "Next-best-action recommendations sliding in with confidence scores",
    },
    mockupShot: {
      src: "/product/crm/recommendation.png",
      alt: "AI recommendation card with next-best action and rationale",
    },
  },
  bi: {
    slug: "bi",
    href: "/modules/bi",
    accent: "aqua",
    statCount: 3,
    capabilityCount: 6,
    relatedAgents: ["Forecast", "Customer Health", "Asset Health", "Carbon Footprint"],
    heroShot: {
      src: "/product/bi/kpi-radar.png",
      alt: "AI-BI/DW KPI radar with color-coded KPI grid",
      aspect: "16/10",
    },
    loopVideo: {
      src: "/product/bi/kpi-radar.webm",
      poster: "/product/bi/kpi-radar.png",
      alt: "KPI grid filtering to critical state, then opening detail sidebar with trend chart and drivers",
    },
    mockupShot: {
      src: "/product/bi/reports.png",
      alt: "Report builder with saved report view",
    },
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Live Workflow (NEW)
// ─────────────────────────────────────────────────────────────────────────────

export const LIVE_WORKFLOW = {
  eyebrow: "LIVE WORKFLOW · CLOSING THE LOOP ON A KPI",
  heading:
    "From threshold breach to closed-loop recovery — without leaving the platform.",
  description:
    "Every agent move is a real API call against the same headless services your people use. Same RBAC, same audit log, same SoD checks. Six steps, five modules, one accountable human in the loop — and a KPI that comes back into range on its own.",
  quote:
    "No black boxes. No nightly batches. No 'export to Excel'. The platform watches the KPI, plans the response, executes through approved tools, and tells you what it did.",
  steps: [
    {
      index: "01",
      label: "DETECT",
      agents: "Cash Position Agent · AI-BI/DW",
      headline: "13-week liquidity forecast breaches its lower bound.",
      body: "The semantic layer evaluates the liquidity KPI continuously against its target. When week-6 cash falls below the policy floor, the orchestrator wakes the specialists wired to that KPI's recovery playbook.",
      metric: "€1.8M projected deficit · week 6",
    },
    {
      index: "02",
      label: "ATTRIBUTE",
      agents: "DSO Agent + Collections Agent · CRM · ERP/AR",
      headline: "Variance is traced to three overdue customers.",
      body: "Drivers are quantified, not guessed: 73% of the variance comes from the top three overdue receivables. The orchestrator drafts a personalised collections sequence per customer through the same APIs your AR clerks use.",
      metric: "€902K · 3 accounts · 28–42 days overdue",
    },
    {
      index: "03",
      label: "PLAN",
      agents: "Working Capital Agent · Procurement · Treasury",
      headline: "Payables are deferred where supplier terms allow.",
      body: "Three suppliers with no early-pay discount and headroom in payment terms are queued for a 10–15 day deferral. The agent checks the contract LCM, supplier SLA, and current ESG/risk score before proposing.",
      metric: "€700K freed · 3 supplier extensions",
    },
    {
      index: "04",
      label: "OPTIMIZE",
      agents: "Inventory Agent · EAM · WMS",
      headline: "Slow-moving stock is staged for liquidation.",
      body: "Six SKUs with over 180-day turn are scored against markdown vs supplier-return scenarios. The recommended mix yields the highest cash return while keeping safety stock for fast-movers intact.",
      metric: "€2.1M working capital recoverable",
    },
    {
      index: "05",
      label: "APPROVE",
      agents: "CFO · Human-in-the-loop · BPM",
      headline: "One screen, one approval, three executions.",
      body: "All three plans are bundled into a single approval ticket with full lineage, SoD checks pre-run, and a one-click 'modify' path. Threshold for full autonomy is configurable per agent and per role.",
      metric: "€4.6M combined plan · 1 approval",
    },
    {
      index: "06",
      label: "EXECUTE & LEARN",
      agents: "Orchestrator · Audit · Knowledge Graph",
      headline: "KPI returns to range; the playbook updates itself.",
      body: "Collections sequences fire, AP runs reschedule, markdowns post — all through the same APIs, all logged. The orchestrator records which moves moved the needle and refines the recovery playbook for next time.",
      metric: "KPI restored · t+11 days · audit complete",
    },
  ] as readonly WorkflowStep[],
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Agentic AI Catalog (NEW)
// ─────────────────────────────────────────────────────────────────────────────

export const AGENT_CATALOG_INTRO = {
  eyebrow: "AGENTIC AI · SPECIALIST CATALOG",
  heading: "A specialist agent for every KPI you care about.",
  description:
    "DivetIQ ships with a catalog of agents - coordinated by an Orchestrator that decomposes cross-functional questions across modules. Every agent uses the same OpenAPI tools your humans use, respects the same RBAC / ABAC permissions, and logs every reasoning trace. EU AI Act ready, with kill-switch per agent and globally.",
} as const;

export const AGENT_CATALOG: readonly AgentCategory[] = [
  {
    category: "Financial",
    agents: [
      "Cash Position",
      "DSO / Collections",
      "DPO / Payment",
      "Revenue & Margin",
      "Budget Variance",
      "Reconciliation",
      "Forecast",
      "Treasury",
      "Subscription Revenue",
    ],
  },
  {
    category: "Operational",
    agents: [
      "Inventory Optimization",
      "Supplier Performance",
      "Asset Health",
      "Maintenance",
      "Process Performance",
      "Order Fulfillment",
      "Field Service",
    ],
  },
  {
    category: "Customer",
    agents: [
      "Customer Health",
      "Pipeline Health",
      "Service Performance",
      "Marketing Performance",
      "Onboarding",
    ],
  },
  {
    category: "People",
    agents: [
      "Workforce Capacity",
      "Retention Risk",
      "Compliance Training",
      "Payroll Validation",
      "Wellbeing",
    ],
  },
  {
    category: "ESG",
    agents: [
      "Carbon Footprint",
      "Energy Optimization",
      "Supplier Sustainability",
      "CSRD Reporting",
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Comparison (NEW)
// ─────────────────────────────────────────────────────────────────────────────

export const COMPARISON_INTRO = {
  eyebrow: "COMPARE",
  heading: "Why teams choose DivetIQ over Competitor 1 and Competitor 2.",
  description:
    "Based on public pricing and product documentation. Specifics, not adjectives.",
} as const;

export const COMPARISON: readonly ComparisonRow[] = [
  {
    row: "Pricing model",
    divetiq: "Pay per Use - metered transactions, API calls, agent actions",
    workday: "Per-seat licensing",
    anaplan: "Per-seat + per-workspace licensing",
  },
  {
    row: "Architecture",
    divetiq: "Headless, API-first (REST + GraphQL + gRPC)",
    workday: "Monolithic suite with UI-coupled APIs",
    anaplan: "Modeling-platform-centric, tight modeling shell",
  },
  {
    row: "Intelligence",
    divetiq: "Specialist AI agents that execute, with audit trace",
    workday: "Illuminate AI add-on",
    anaplan: "PlanIQ - mostly forecasting",
  },
  {
    row: "Module breadth",
    divetiq: "8 modules under one semantic layer",
    workday: "Strong HCM/Fin, weaker EAM and Procurement",
    anaplan: "Planning-only",
  },
  {
    row: "Time to value",
    divetiq: "Phase 1 in 1–4 weeks",
    workday: "6–18 months typical",
    anaplan: "3–9 months",
  },
  {
    row: "Data sovereignty",
    divetiq: "EU Sovereign Cloud (GAIA-X aligned), air-gap option",
    workday: "US multi-tenant default",
    anaplan: "US multi-tenant default",
  },
  {
    row: "Lock-in posture",
    divetiq: "Open table formats (Iceberg, Delta), zero lock-in tagline",
    workday: "Proprietary data model",
    anaplan: "Proprietary modeling DSL",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Pay-per-Use (NEW)
// ─────────────────────────────────────────────────────────────────────────────

export const PAY_PER_USE = {
  eyebrow: "PAY PER USE",
  heading: "Usage in. Bill out. No mystery.",
  description:
    "Three commitments make Pay per Use real instead of marketing copy.",
  cards: [
    {
      number: "01",
      kicker: "Meter what creates value",
      title: "Per-transaction. Per-API-call. Per-agent-action. Per-GB.",
      body: "No seat counts. No phantom users. Every employee can be onboarded without budget approval - they only show up on the bill if they actually use a chargeable capability.",
    },
    {
      number: "02",
      kicker: "Stop what isn't working",
      title: "Modules turn on and off independently.",
      body: "Pause CRM during off-season, scale Procurement during sourcing windows. Storage tiers (hot / warm / cold) priced separately. Agent autonomy thresholds priced into the action - no flat-fee black box.",
    },
    {
      number: "03",
      kicker: "See it before you sign it",
      title: "Real-time cost observability.",
      body: "Per module, per team, per workflow, per query. Anomalous-cost alerts. Dry-run estimates for every reverse-ETL pipeline before it touches production.",
    },
  ] as readonly PayCard[],
  invoice: {
    header: "DivetIQ · Sample Monthly Invoice · 250-employee mid-market customer · March 2026",
    lines: [
      { module: "HCM", detail: "247 active employees + 1 payroll run", amount: "€1,234.50" },
      { module: "Procurement", detail: "4,800 invoices matched + 12 sourcing events", amount: "€890.00" },
      { module: "ERP", detail: "92,400 journal entries + 3,200 sales orders", amount: "€1,470.30" },
      { module: "BI / DW", detail: "1.2 TB scanned + 18,400 conversational queries", amount: "€640.20" },
      { module: "CRM", detail: "12,800 active contacts + 38,000 emails sent", amount: "€512.40" },
      { module: "EAM", detail: "1,140 active assets + 720 work orders", amount: "€388.10" },
      { module: "ECM", detail: "740 GB hot + 4.2 TB cold + 3,200 OCR pages", amount: "€212.80" },
      { module: "Agents", detail: "1,840 agent actions + 90 human-in-loop approvals", amount: "€296.50" },
    ] as readonly InvoiceLine[],
    total: "€5,644.80",
  },
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Trust strip / social proof (replaces fake testimonials)
// ─────────────────────────────────────────────────────────────────────────────

export const TESTIMONIALS_INTRO = {
  eyebrow: "BUILT ON STANDARDS",
  heading: "Backed by certifications, not promises.",
  description:
    "Compliance and partner-stack signals while we onboard our first design partners.",
} as const;

export const CERT_BADGES = [
  "SOC 2 Type II",
  "ISO 27001",
  "GDPR",
  "EU Sovereign Cloud",
  "OpenAPI 3.1",
  "BPMN 2.0",
] as const;

export const PARTNER_LOGOS = [
  "Microsoft",
  "Stripe",
  "DocuSign",
  "Apache Iceberg",
  "Anthropic",
  "OpenTelemetry",
  "Power BI",
  "Tableau",
  "Apache Kafka",
  "PostgreSQL",
] as const;

// Phase 1 ships with no real testimonials. Component checks length === 0
// and renders the cert + marquee row instead.
export const TESTIMONIALS: readonly Testimonial[] = [];

// ─────────────────────────────────────────────────────────────────────────────
// CTA Band
// ─────────────────────────────────────────────────────────────────────────────

export const CTA_BAND = {
  heading: "Stop renewing licenses.",
  subheading: "Start paying for outcomes.",
  description:
    "DivetIQ - one Headless Software Solution, eight modules, an AI Agentic Workflow for KPI Management, billed Pay per Use.",
  primary: { label: "Get a usage estimate", href: "/contact" },
  secondary: { label: "Talk to an architect", href: "/contact" },
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Footer
// ─────────────────────────────────────────────────────────────────────────────

export const FOOTER_COLUMNS: readonly FooterColumn[] = [
  {
    heading: "Product",
    links: [
      { label: "Platform", href: "/#platform" },
      { label: "AI Agents", href: "/#agents" },
      { label: "Pricing", href: "/#pricing" },
      { label: "Architecture", href: "/", stub: true },
      { label: "Changelog", href: "/", stub: true },
    ],
  },
  {
    heading: "Resources",
    links: [
      { label: "Blog", href: "/blog" },
      { label: "Docs", href: "/", stub: true },
      { label: "Status", href: "/", stub: true },
      { label: "FAQ", href: "/faq" },
    ],
  },
  {
    heading: "Company",
    links: [
      { label: "About", href: "/", stub: true },
      { label: "Contact", href: "/contact" },
      { label: "Careers", href: "/", stub: true },
    ],
  },
  {
    heading: "Trust",
    links: [
      { label: "Trust Center", href: "/", stub: true },
      { label: "Privacy", href: "/", stub: true },
      { label: "Terms", href: "/", stub: true },
      { label: "DPA", href: "/", stub: true },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Blog
// ─────────────────────────────────────────────────────────────────────────────

export const BLOG_CATEGORIES = [
  "All Categories",
  "Customer Service",
  "Support Toolkit",
  "Growth & Culture",
  "Product Updates",
] as const;

// ─────────────────────────────────────────────────────────────────────────────
// FAQ - homepage compact + standalone /faq page
// ─────────────────────────────────────────────────────────────────────────────

export const FAQ_HERO = {
  badge: "FAQs",
  heading: ["Questions and", "answers"] as const,
  description:
    "Headless platform, agentic workflows, pay-per-use pricing - plus everything else mid-market buyers ask before booking a demo.",
  searchPlaceholder: "Search questions",
} as const;

export const FAQ_TOPICS = ["General"] as const;

export const FAQ_INTRO = {
  eyebrow: "FREQUENTLY ASKED",
  heading: "Questions and answers.",
} as const;

export const FAQ_ITEMS: readonly FaqItem[] = [
  {
    topic: "General",
    question: 'What does "Headless Software Solution" mean for DivetIQ?',
    answer:
      "Every module exposes its capabilities through APIs and events - no hardcoded UI. Customers consume DivetIQ through any front-end: web, mobile, partner portals, embedded widgets, voice, or AI agents.",
  },
  {
    topic: "General",
    question: "How does the AI Agentic Workflow for KPI Management work?",
    answer:
      "Specialist agents continuously evaluate KPIs against thresholds, attribute variance across modules, draft remediation, and execute it through the same APIs human users use - within governance limits the customer configures per agent, per role.",
  },
  {
    topic: "General",
    question: "What is included in Pay per Use pricing?",
    answer:
      "Transactions, API calls, agent actions, storage tiers, analytical scans and integration runs are metered. Modules can be turned on or off independently. Real-time cost observability is included.",
  },
  {
    topic: "General",
    question: "Which modules can I deploy first?",
    answer:
      "Any. The eight modules - Procurement, ECM/DMS, HCM with Payroll & Workforce, EAM with Inventory, EPM, AI-Based ERP, CRM, and the AI-Based BI & Data Warehouse - are independently deployable.",
  },
  {
    topic: "General",
    question: "Is DivetIQ deployable in the EU under sovereignty rules?",
    answer:
      "Yes - public cloud SaaS, single-tenant SaaS, private cloud (AWS / Azure / GCP), on-premise Kubernetes / OpenShift, hybrid, EU Sovereign Cloud (GAIA-X aligned), and air-gapped private cloud are all supported.",
  },
  {
    topic: "General",
    question: "Which BI tools is the platform compatible with?",
    answer:
      "Power BI, Tableau, Looker, Excel, Google Sheets, plus any JDBC/ODBC client. The lakehouse is also directly readable by Spark, Trino, DuckDB, Snowflake, BigQuery and Databricks under governed share controls.",
  },
  {
    topic: "General",
    question: "How long does a first deployment take?",
    answer:
      "Phase 1 - Foundation: 1–4 weeks (tenant provisioned, CDC connected from active modules, bronze and silver layers populated, core semantic-layer entities live, first 20 dashboards in production, conversational analytics enabled in trial mode). Phase 2 - Adoption: 5–10 weeks. Phase 3 - Embedding: 8–14 weeks. Phase 4 - Differentiation: 12–20 weeks.",
  },
  {
    topic: "General",
    question: "Who is DivetIQ built for?",
    answer:
      "Medium-sized companies - approximately 100 to 2,500 employees, EUR 20–500 million annual revenue, up to 25 legal entities. The feature surface deliberately excludes Fortune-500-only complexity and adds genuinely-needed mid-market capabilities that enterprise vendors charge for but rarely deliver well at this scale.",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Contact
// ─────────────────────────────────────────────────────────────────────────────

export const CONTACT_COPY = {
  heading: "Let's talk.",
  description:
    "Book a demo, request a usage estimate for your business, or send a question. A DivetIQ architect will respond within one business day.",
  submit: "Send Message",
  successHeading: "Thanks - we'll be in touch",
  successDescription:
    "We've received your message and a member of our team will get back to you within one business day.",
  fields: {
    fullname: { label: "Full name", placeholder: "Jane Doe" },
    email: { label: "Email", placeholder: "you@company.com" },
    message: { label: "Message", placeholder: "What KPI are you trying to fix?" },
  },
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// 404
// ─────────────────────────────────────────────────────────────────────────────

export const NOT_FOUND_COPY = {
  heading: "Sorry About That",
  description: "The page you're looking for doesn't exist or has moved",
  cta: { label: "Turn Around", href: "/" },
} as const;
